import { IconBase } from '@phosphor-icons/react';
import classNames from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';

export interface TabItem {
  key: string;
  label: string;
  description: string;
  route: string;
  icon?: any;
  exact?: boolean;
  badge?: number;
}

export default function HorizontalTabs({
  tabs,
  renderSecondaryColours,
  disableActiveHighlight,
}: {
  tabs: TabItem[];
  renderSecondaryColours?: boolean;
  disableActiveHighlight?: boolean;
}) {
  const location = useLocation();

  return (
    <nav className="flex gap-x-1 overflow-x-auto" aria-label="Tabs">
      {tabs.map(tab => {
        function isActive() {
          if (disableActiveHighlight) return false;
          if (tab?.exact) {
            return location.pathname === tab.route;
          }
          return (
            location.pathname === tab.route ||
            (location.pathname.startsWith(tab.route) && tab.route !== '/')
          );
        }

        const iconClassName = classNames(
          isActive()
            ? renderSecondaryColours
              ? 'text-gray-100'
              : 'text-indigo-600'
            : renderSecondaryColours
            ? 'text-indigo-400 group-hover:text-gray-600'
            : 'text-gray-600 group-hover:text-indigo-600',
          'shrink-0'
        );

        return (
          <NavLink
            key={tab.key}
            to={tab.route}
            replace
            className={classNames(
              isActive()
                ? renderSecondaryColours
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-indigo-100 text-indigo-700'
                : renderSecondaryColours
                ? 'text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 font-bold'
                : ' text-gray-600 hover:text-gray-800 hover:bg-gray-50',
              'rounded-md px-3 py-2 text-sm font-medium text-nowrap min-w-fit'
            )}
            aria-current={isActive() ? 'page' : undefined}
          >
            <div className="flex items-center gap-1">
              {tab.icon &&
                (typeof tab.icon === 'string' ? (
                  <img
                    src={tab.icon}
                    alt=""
                    className={classNames(iconClassName, 'h-5 w-5')}
                    aria-hidden
                  />
                ) : (
                  (() => {
                    const TabIcon = tab.icon;
                    return (
                      <TabIcon
                        size={20}
                        weight={isActive() ? 'fill' : 'regular'}
                        color="currentColor"
                        className={iconClassName}
                        aria-hidden
                      />
                    );
                  })()
                ))}
              {tab.label}
              {typeof tab.badge === 'number' && (
                <span
                  className={classNames(
                    isActive()
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-600 group-hover:bg-gray-300',
                    'ml-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums'
                  )}
                >
                  {tab.badge}
                </span>
              )}
            </div>
          </NavLink>
        );
      })}
    </nav>
  );
}
